import { api } from './axios'
import type { MatriculaListItem, MatriculaDetalhes, BancoDoParceiro, ConfiguracaoParceiro, UsuarioParceiroItem, DashboardData, Parceiro, Captador } from '@/types'

export async function getParceiro(cpfCnpj: string) {
  const response = await api.get('/parceiros', { params: { cpfCnpj } })
  return response.data
}

export async function aceitarContrato(body: {
  cpfCnpj: string
  cpfUsuarioAceite: string
  nome: string
  telefone: string
  uf: string
}) {
  const response = await api.post('/parceiros/contrato/aceite', body)
  return response.data
}

export type AndamentoDoCurso =
  | 'NAO_INICIADO'
  | 'EM_ANDAMENTO'
  | 'AGUARDANDO_DETRAN'
  | 'FINALIZADO'
export type StatusPagamento = 'PAGO' | 'PENDENTE' | 'VENCIDO'

export interface AlunosFilters {
  limit: number
  pageNumber: number
  nomeAluno?: string
  cpf?: string
  cursoId?: string
  andamento?: AndamentoDoCurso
  pagamento?: StatusPagamento
  createdAtStart?: string
  createdAtEnd?: string
}

export async function getConfig() {
  const response = await api.get<ConfiguracaoParceiro>('/parceiros/config')
  return response.data
}

export async function updateConfig(values: {
  banco?: BancoDoParceiro
  aceitaCartao?: boolean
}) {
  const response = await api.put('/parceiros/config', values)
  return response.data
}

export async function salvarDadosDeRepasse(values: { payeeCode: string; numeroConta: string }) {
  const response = await api.put<{ success: true; payeeCode: string; numeroConta: string }>(
    '/parceiros/dados-de-repasse',
    values,
  )
  return response.data
}

export async function uploadLogo(file: File) {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<{ logoUrl: string }>('/parceiros/logo', form)
  return response.data
}

export async function removerLogo() {
  const response = await api.delete('/parceiros/logo')
  return response.data
}

export async function getUsuariosParceiro() {
  const response = await api.get<UsuarioParceiroItem[]>('/parceiros/usuarios-parceiro')
  return response.data
}

export async function criarUsuarioParceiro(values: {
  nome: string
  email: string
  cpf: string
  role: 'admin' | 'operador'
}) {
  const response = await api.post<{ success: boolean; usuarioId: number }>(
    '/parceiros/usuarios-parceiro',
    values,
  )
  return response.data
}

export async function atualizarRoleUsuarioParceiro(usuarioId: number, role: 'admin' | 'operador') {
  const response = await api.put(`/parceiros/usuarios-parceiro/${usuarioId}`, { role })
  return response.data
}

export async function removerUsuarioParceiro(usuarioId: number) {
  const response = await api.delete(`/parceiros/usuarios-parceiro/${usuarioId}`)
  return response.data
}

export async function getAluno(matriculaId: number) {
  const response = await api.get<MatriculaDetalhes>(`/parceiros/alunos/${matriculaId}`)
  return response.data
}

export async function getAlunos(filters: AlunosFilters): Promise<{
  data: MatriculaListItem[]
  currentPage: number
  totalRecords: number
  limit: number
}> {
  const response = await api.get<{
    data: MatriculaListItem[]
    currentPage: number
    totalRecords: number
    limit: number
  }>('/parceiros/alunos', { params: filters })
  return response.data
}

export interface MatriculaDoAluno {
  id: number
  cursoNome: string
  statusDetran: string
  createdAt: string
  pago: boolean
  alunoNome: string
  parceiroNome: string
}

export async function buscarMatriculasPorCpf(cpf: string): Promise<MatriculaDoAluno[]> {
  const response = await api.get<MatriculaDoAluno[]>('/parceiros/alunos/por-cpf', {
    params: { cpf },
  })
  return response.data
}

export async function createCobrancaAgregada(matriculaIds: number[]) {
  const response = await api.post<{ id: number; valor: number; infoPix: { pix: string } }>(
    '/parceiros/cobranca-agregada',
    { matriculaIds },
  )
  return response.data
}

/** Fields the backend could read off the CNH. Every one is optional — a partial read is still useful. */
export interface CamposDaCnh {
  nome?: string
  cpf?: string
  dataNascimento?: string
  cnh?: string
  categoriaCnh?: string
  dataPrimeiraHabilitacao?: string
}

export interface CnhExtraida {
  campos: CamposDaCnh
  /** Per field: the OCR confidence and which source produced it (label, MRZ or a document sweep). */
  detalhes: Partial<
    Record<keyof CamposDaCnh, { confianca: number; origem: 'rotulo' | 'mrz' | 'documento' }>
  >
}

export async function extrairCnh(file: File) {
  const form = new FormData()
  form.append('file', file)
  const response = await api.post<CnhExtraida>('/parceiros/extrair-cnh', form)
  return response.data
}

export async function getDashboard(
  periodStart: string,
  periodEnd: string,
  comparisonStart: string,
  comparisonEnd: string,
) {
  const response = await api.get<DashboardData>('/parceiros/dashboard', {
    params: { start: periodStart, end: periodEnd, comparisonStart, comparisonEnd },
  })
  return response.data
}

export async function getCobrancaAgregada(id: number) {
  const response = await api.get<{ id: number; status: string; dataPagamento: string | null }>(
    `/parceiros/cobranca-agregada/${id}`,
  )
  return response.data
}

export interface ContaDeRecebimentoDaVenda {
  apto: boolean
  /** Banco do VENDEDOR, que é quem decide o provedor da cobrança — não o do parceiro logado. */
  banco: BancoDoParceiro
  pendentes: Array<{ id: number; nome: string; ehVoce: boolean }>
}

export async function verificarContaDeRecebimento(
  vendedorId?: number
): Promise<ContaDeRecebimentoDaVenda> {
  const response = await api.get<ContaDeRecebimentoDaVenda>('/parceiros/conta-de-recebimento', {
    params: vendedorId ? { vendedorId } : {},
  })
  return response.data
}

export async function listarCaptadores(): Promise<Captador[]> {
  const response = await api.get<Captador[]>('/parceiros/captadores')
  return response.data
}

export async function listarTodosParceiros() {
  const response = await api.get<Array<{ id: number; cpfCnpj: string; nome: string }>>('/parceiros/lista')
  return response.data
}

export async function selecionarParceiro(parceiroId: number) {
  const response = await api.post<{ parceiro: Parceiro }>('/parceiros/selecionar-parceiro', {
    parceiroId,
  })
  return response.data
}
